import { getSupabaseClient } from "@/lib/supabaseClient";

/* SMART DEMO — Public frontend tracking (FAZ 2)
   Yalnız RPC çağrıları yapar, hiçbir sh_* tabloya doğrudan SELECT/INSERT/UPDATE/DELETE yapmaz.
   Token yoksa veya herhangi bir hata olursa demo %100 normal çalışmaya devam eder. */

const TOKEN_STORAGE_KEY = "smart_demo_tracking_token";

export type TrackingDemoMode = "custom" | "ready";

type TrackPayload = Record<string, unknown>;

interface RpcResult {
  ok?: boolean;
  error?: string;
}

let trackingToken: string | null = null;
let trackingEnabled = false;
let trackingError: string | null = null;
let demoMode: TrackingDemoMode = "custom";

const emittedEventKeys = new Set<string>();
let completionInFlight = false;
let completionSent = false;

/* Attempt (deneme) — aynı lead aynı oturumda birden fazla kez deneyebilir.
   Her gerçek deneme başlangıcı yeni bir attempt_id alır. attempt_id yalnız
   module-memory'de tutulur (refresh zaten landing'e döner, yanlışlıkla yeni
   attempt üretmez). Deterministik değil, yalnız benzersiz olması yeterli. */
let currentAttemptId: string | null = null;
let attemptSeq = 0;

function generateAttemptId(): string {
  attemptSeq += 1;
  const rand = Math.random().toString(36).slice(2, 8);
  return `att-${Date.now().toString(36)}-${attemptSeq}-${rand}`;
}

function parseTokenFromUrl(): string | null {
  try {
    const raw = new URLSearchParams(window.location.search).get("t");
    if (!raw) return null;
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : null;
  } catch {
    return null;
  }
}

/* Geçersiz / süresi dolmuş oturum hatası -> tracking'i sessizce kapat, demo devam eder. */
function handleSessionError(message?: string): void {
  if (message && (message.includes("oturum") || message.includes("Token"))) {
    trackingEnabled = false;
  }
}

export function initSmartDemoTracking(): void {
  const token = parseTokenFromUrl();
  if (!token) {
    trackingToken = null;
    trackingEnabled = false;
    return;
  }
  trackingToken = token;
  trackingEnabled = true;
  try {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } catch {
    // localStorage erişimi yoksa sessizce devam et.
  }
}

export function isTrackingEnabled(): boolean {
  return trackingEnabled;
}

export function getTrackingToken(): string | null {
  return trackingToken;
}

export function getTrackingError(): string | null {
  return trackingError;
}

export function setDemoMode(mode: TrackingDemoMode): void {
  demoMode = mode;
}

export function getDemoMode(): TrackingDemoMode {
  return demoMode;
}

/* Yeni bir deneme (attempt) başlatır ve id'sini döner. Yalnız gerçek kullanıcı
   aksiyonlarından (custom başlatma / hazır preset seçme) çağrılır — re-render
   veya effect değil. Böylece refresh/StrictMode yanlışlıkla yeni attempt üretmez. */
export function beginAttempt(): string {
  currentAttemptId = generateAttemptId();
  return currentAttemptId;
}

export function getCurrentAttemptId(): string | null {
  return currentAttemptId;
}

async function callEventRpc(
  eventKey: string,
  eventType: string,
  payload: TrackPayload,
): Promise<void> {
  const client = getSupabaseClient();
  if (!client) {
    trackingError = "Supabase client yapılandırılamadı.";
    return;
  }
  try {
    const { data, error } = await client.rpc("fn_smart_demo_public_event_v1", {
      p_token: trackingToken,
      p_event_key: eventKey,
      p_event_type: eventType,
      p_payload: payload,
    });
    if (error) {
      trackingError = error.message;
      return;
    }
    const result = data as RpcResult | null;
    if (result && result.ok === false) {
      trackingError = result.error ?? "Bilinmeyen takip hatası";
      handleSessionError(result.error);
    }
  } catch (err) {
    trackingError = err instanceof Error ? err.message : String(err);
  }
}

/* Fire-and-forget. UI transition'ı asla bekletmez; aynı event_key yalnız bir kez gönderilir. */
export function trackDemoEvent(
  eventKey: string,
  eventType: string,
  payload: TrackPayload = {},
): void {
  if (!trackingEnabled || !trackingToken) return;
  // Aynı oturumda birden fazla deneme (attempt) olabildiği için idempotency key'i
  // attempt bazında benzersizleştirilir. Böylece aynı event farklı attempt'lerde
  // yeniden gönderilebilirken, aynı attempt içindeki re-render/retry tek seferlik kalır.
  // Backend ON CONFLICT (oturum_id, event_key) dedup'ı da bu benzersiz key üzerinden çalışır.
  const dedupKey = currentAttemptId ? `${currentAttemptId}:${eventKey}` : eventKey;
  if (emittedEventKeys.has(dedupKey)) return;
  emittedEventKeys.add(dedupKey);
  const finalPayload = currentAttemptId
    ? { ...payload, attempt_id: currentAttemptId }
    : payload;
  void callEventRpc(dedupKey, eventType, finalPayload);
}

export interface CompleteInput {
  finalScore: number;
  finalIntent: string;
  selectedService: string | null;
  selectedCtaId: string | null;
  selectedCtaLabel: string | null;
}

async function attemptComplete(input: CompleteInput): Promise<boolean> {
  const client = getSupabaseClient();
  if (!client) {
    trackingError = "Supabase client yapılandırılamadı.";
    return false;
  }
  try {
    const { data, error } = await client.rpc("fn_smart_demo_public_complete_v1", {
      p_token: trackingToken,
      p_demo_modu: demoMode,
      p_final_score: input.finalScore,
      p_final_intent: input.finalIntent,
      p_secilen_hizmet: input.selectedService,
      p_secilen_cta_id: input.selectedCtaId,
      p_secilen_cta_label: input.selectedCtaLabel,
    });
    if (error) {
      trackingError = error.message;
      return false;
    }
    const result = data as RpcResult | null;
    if (result && result.ok === true) return true;
    if (result && result.ok === false) {
      trackingError = result.error ?? "Bilinmeyen tamamlama hatası";
      handleSessionError(result.error);
    }
    return false;
  } catch (err) {
    trackingError = err instanceof Error ? err.message : String(err);
    return false;
  }
}

/* Completion — tek çağrı + en fazla bir kontrollü retry. UI'yi kilitlemez, backend idempotent. */
export async function trackDemoComplete(input: CompleteInput): Promise<void> {
  if (!trackingEnabled || !trackingToken) return;
  if (completionSent || completionInFlight) return;

  completionInFlight = true;
  let success = false;
  try {
    success = await attemptComplete(input);
    if (!success && trackingEnabled && !completionSent) {
      await new Promise((resolve) => window.setTimeout(resolve, 1200));
      success = await attemptComplete(input);
    }
    if (success) completionSent = true;
  } finally {
    completionInFlight = false;
  }
}

/* Yönetici WhatsApp numarası — public token üzerinden RPC ile alınır.
   Numara asla frontend'e hardcode edilmez; yalnız server-side ayar tablosundan gelir. */
export interface ManagerContactResult {
  ok: boolean;
  phone?: string;
  error?: string;
}

export async function fetchManagerContact(): Promise<ManagerContactResult | null> {
  if (!trackingEnabled || !trackingToken) {
    trackingError = "Takip oturumu yok";
    return null;
  }
  const client = getSupabaseClient();
  if (!client) {
    trackingError = "Supabase client yapılandırılamadı.";
    return null;
  }
  try {
    const { data, error } = await client.rpc("fn_smart_demo_public_manager_contact_v1", {
      p_token: trackingToken,
    });
    if (error) {
      trackingError = error.message;
      return null;
    }
    const result = data as ManagerContactResult | null;
    if (!result) return null;
    if (result.ok === false) {
      trackingError = result.error ?? "Yönetici iletişim bilgisi alınamadı";
      handleSessionError(result.error);
      return null;
    }
    return result;
  } catch (err) {
    trackingError = err instanceof Error ? err.message : String(err);
    return null;
  }
}